import React from "react";

/**
 * V1.8.8 — Shared SVG <defs> with hatching patterns for the 5 supported
 * wall materials. Inlined inside any SVG that needs to reference the
 * patterns by id — SVG pattern IDs are document-local so we can't share
 * them across separate <svg> elements (the canvas + the legend swatches).
 *
 * Pattern IDs (kept in sync with MATERIAL_CATALOG.hatch in lib/walls.js):
 *   hatch-wood      Wood Frame — diagonal stripes (45°)
 *   hatch-steel     Steel Stud — dashed parallel lines (offset)
 *   hatch-cmu       CMU — diagonal cross-hatch
 *   hatch-brick     Brick Veneer — staggered brick rows
 *   hatch-concrete  Concrete — dotted speckle
 *
 * `mode` ∈ "diagram" | "architectural" controls the line color so the
 * patterns read clearly on the dark Diagram (cyan grid) background AND on
 * the warm cream Architectural background.
 */
export default function HatchDefs({ mode = "diagram", idSuffix = "" }) {
  const stroke = mode === "architectural" ? "#1f2937" : "#a8a29e";
  const brickFill = mode === "architectural" ? "#7c2d12" : "#9a3412";
  const dotFill = mode === "architectural" ? "#52525b" : "#a8a29e";
  // idSuffix lets the caller scope IDs to a specific SVG instance when
  // both the canvas + the legend live on the same page (avoids ID
  // collisions in the DOM). FloorPlan2D passes "" for the canvas (default
  // IDs match MATERIAL_CATALOG.hatch values). The Legend reuses the same
  // IDs in its own per-row swatch SVGs — that's fine, IDs are SVG-local.
  const id = (k) => `${k}${idSuffix}`;
  return (
    <defs>
      <pattern id={id("hatch-wood")} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke={stroke} strokeWidth="0.7" />
      </pattern>
      <pattern id={id("hatch-steel")} width="6" height="3" patternUnits="userSpaceOnUse">
        <line x1="0" y1="1.5" x2="3" y2="1.5" stroke={stroke} strokeWidth="0.7" />
      </pattern>
      <pattern id={id("hatch-cmu")} width="6" height="6" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="6" y2="6" stroke={stroke} strokeWidth="0.6" />
        <line x1="6" y1="0" x2="0" y2="6" stroke={stroke} strokeWidth="0.6" />
      </pattern>
      <pattern id={id("hatch-brick")} width="12" height="6" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="12" height="6" fill="none" />
        {/* Top course bricks */}
        <rect x="0" y="0"   width="6" height="3" fill={brickFill} opacity="0.55" stroke={stroke} strokeWidth="0.4" />
        <rect x="6" y="0"   width="6" height="3" fill={brickFill} opacity="0.40" stroke={stroke} strokeWidth="0.4" />
        {/* Staggered lower course */}
        <rect x="-3" y="3"  width="6" height="3" fill={brickFill} opacity="0.45" stroke={stroke} strokeWidth="0.4" />
        <rect x="3"  y="3"  width="6" height="3" fill={brickFill} opacity="0.55" stroke={stroke} strokeWidth="0.4" />
        <rect x="9"  y="3"  width="6" height="3" fill={brickFill} opacity="0.40" stroke={stroke} strokeWidth="0.4" />
      </pattern>
      <pattern id={id("hatch-concrete")} width="6" height="6" patternUnits="userSpaceOnUse">
        <circle cx="1.5" cy="1.5" r="0.7" fill={dotFill} />
        <circle cx="4.5" cy="3.5" r="0.6" fill={dotFill} />
        <circle cx="2.5" cy="4.8" r="0.5" fill={dotFill} />
      </pattern>
    </defs>
  );
}
